import React, { useEffect, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupIcon from '@mui/icons-material/Group';
import {
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import dayjs from 'dayjs';
import { useParams } from 'react-router';

import Chip from '../../components/Chip';
import ContentCard from '../../components/ContentCard';
import ContentWrapper from '../../components/ContentWrapper';
import CopyButton from '../../components/CopyButton';
import DataGrid from '../../components/DataGrid';
import PageContent from '../../components/PageContent';
import SkeletonStack from '../../components/SkeletonStack';
import { useGetLessonQuery, useGetLessonsQuery } from '../../services/classes';
import { Lesson } from '../../services/classes/types';
import { useGetCoursesQuery } from '../../services/courses';
import { useGetStaffsQuery } from '../../services/staff';
import { useGetStudentQuery } from '../../services/students';
import { UserGroup } from '../../types/common';
import { getLessonLabel } from '../../utils/common/classes';

import RescheduleLessonDetails from './RescheduleLessonDetails';

const DATA_GRID_COLUMNS = [
  {
    field: 'date',
    headerName: 'Date',
    width: 100,
  },
  {
    field: 'day',
    headerName: 'Day',
    width: 60,
  },
  {
    field: 'time',
    headerName: 'Time',
    width: 100,
  },
  {
    field: 'teacher',
    headerName: 'Teacher',
    width: 120,
  },
  {
    field: 'studentCount',
    headerName: 'Students',
    renderHeader: () => (
      <GroupIcon />
    ),
    width: 60,
  },
  {
    field: 'warning',
    headerName: '',
    width: 180,
    renderCell: (params: any) => (
      params.value && <Chip color="lightRed" label={params.value} size="small" />
    ),
  },
];

const AdminReschedulePage = () => {
  const { subject,
    classId,
    studentId,
    lesson: encodedLesson } = useParams();

  const lesson = decodeURIComponent(encodedLesson ?? '');

  const { data: student, isLoading: isStudentLoading } = useGetStudentQuery({ studentId: studentId ?? '' });

  const { data: teachers, isLoading: isTeachersLoading } = useGetStaffsQuery({
    group: UserGroup.Teachers,
  });

  const { data: courses, isLoading: isCoursesLoading } = useGetCoursesQuery({});

  const course = lesson?.split('#')[0];
  const lessonId = lesson?.split('#')[1];

  const { data: makeUpLessons, isLoading: isMakeUpLessonsLoading } = useGetLessonsQuery({
    course,
    lessonId,
    rollMarked: false,
  });

  const { data: fetchedLesson, isLoading: isLessonLoading } = useGetLessonQuery({
    subject: subject ?? '',
    classId: classId ?? '',
    lesson,
  });

  const [makeUpLessonRows, setMakeUpLessonRows] = useState<any[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | undefined>();
  const [makeUpOptionsCopyText, setMakeUpOptionsCopyText] = useState<string>('');
  const [selectedRows, setSelectedRows] =
    useState<GridRowSelectionModel>([]);

  const handleSelect = (l?: Lesson) => {
    if (!l) {
      setSelectedLesson(undefined);
      return;
    }

    if (selectedLesson?.id === l.id) {
      setSelectedLesson(undefined);
    } else {
      setSelectedLesson(l);
    }
  };

  useEffect(() => {
    if (makeUpLessons && teachers) {
      // Set make up lesson rows but filter out the original lesson
      setMakeUpLessonRows(makeUpLessons.data.filter((l) => l.id !== fetchedLesson?.data.id)
        .sort((a, b) => a.startDatetime - b.startDatetime)
        .map((l) => {
          const weekOfMakeUp = dayjs(l.startDatetime).startOf('week');
          const weekOfOriginal = dayjs(fetchedLesson?.data.startDatetime).startOf('week');

          const weeksDifference = weekOfMakeUp.diff(weekOfOriginal, 'week');
          let warning = '';

          if (weeksDifference < 0) {
            warning = `${-weeksDifference} week${-weeksDifference > 1 ? 's' : ''} before lesson`;
          } else if (weeksDifference > 0) {
            warning = `${weeksDifference} week${weeksDifference > 1 ? 's' : ''} after lesson`;
          } else {
            warning = '';
          }

          return {
            id: l.id,
            date: dayjs(l.startDatetime).format('D MMM \'YY'),
            day: dayjs(l.startDatetime).format('ddd'),
            time: dayjs(l.startDatetime).format('h:mm a'),
            teacher: teachers.data.find((t) => t.id === l.teacher)?.name ?? 'Unassigned',
            studentCount: l.enrolledStudents.length,
            warning,
          };
        }));
    }
  }, [makeUpLessons, teachers]);

  if (
    isMakeUpLessonsLoading ||
    isTeachersLoading ||
    isStudentLoading ||
    isLessonLoading ||
    isCoursesLoading
  ) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <PageContent
      sideBar={(selectedLesson && fetchedLesson) ? (
        <RescheduleLessonDetails
          selectedLesson={selectedLesson}
          onClose={() => setSelectedLesson(undefined)}
          studentId={studentId ?? ''}
          originalLesson={fetchedLesson.data}
        />
      ) : <div />}
      sideBarOpen={!!selectedLesson}
      sideBarWidth={600}
    >
      <Stack direction="column" spacing={3}>
        <ContentWrapper backButtonHref={-1} title={`Reschedule ${student?.data.name}`}>
          <Grid container spacing={2} maxWidth={400}>
            <Grid item xs={4}>
              <Typography fontWeight="bold">Lesson</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>
                {(fetchedLesson && courses) && getLessonLabel(fetchedLesson?.data, courses.data)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography fontWeight="bold">Original Date</Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography>
                {fetchedLesson && dayjs(fetchedLesson.data.startDatetime).format('h:mm a D MMM \'YY')}
              </Typography>
            </Grid>
          </Grid>
        </ContentWrapper>
        <ContentCard title="Available Lessons" variant="outlined">
          <CopyButton
            copyText={makeUpOptionsCopyText}
            buttonLabel="Copy Options"
            size="small"
            startIcon={<ContentCopyIcon />}
            disabled={!makeUpOptionsCopyText}
          />
          <DataGrid
            rows={makeUpLessonRows}
            columns={DATA_GRID_COLUMNS}
            getRowId={(e) => e.id}
            loading={isMakeUpLessonsLoading}
            onRowClick={(e) =>
              handleSelect(makeUpLessons?.data?.find((l) => l.id === e.row.id) ?? undefined)}
            hideFooter
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setSelectedRows(newRowSelectionModel);
              setMakeUpOptionsCopyText(newRowSelectionModel.map((id) => {
                const makeUpLesson = makeUpLessonRows.find((l) => l.id === id);
                return ` - ${makeUpLesson?.day} ${makeUpLesson?.time}, ${makeUpLesson?.date} (${makeUpLesson?.teacher})`;
              },
              ).join('\n'));
            }}
            rowSelectionModel={selectedRows}
          />
        </ContentCard>
      </Stack>
    </PageContent>
  );
};

export default AdminReschedulePage;
